/**
 * AdminDeliveryManagement Component
 * Admin panel for managing delivery service providers
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Switch } from '../ui/switch';
import {
  Truck,
  Plus,
  Edit2,
  Trash2,
  Star,
  Phone,
  Mail,
  MapPin,
  Package,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
  TrendingUp,
  Clock,
  Bike,
  Car,
  ExternalLink,
} from 'lucide-react';
import axios from 'axios';
import { useCountry } from '../../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COUNTRIES = [
  { code: 'SL', name: 'Sri Lanka' },
  { code: 'UK', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
  { code: 'IN', name: 'India' },
  { code: 'AE', name: 'UAE' },
];

const AdminDeliveryManagement = () => {
  const { formatCurrency } = useCountry();
  const [activeTab, setActiveTab] = useState('providers');
  const [selectedCountry, setSelectedCountry] = useState('SL');
  const [providers, setProviders] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [formData, setFormData] = useState({
    business_name: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    country: 'SL',
    coverage_areas: '',
    pricing_per_km: 25,
    base_price: 150,
    is_third_party: false,
    third_party_api: '',
    services: ['pickup', 'delivery'],
    vehicle_types: ['bike', 'car'],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, [selectedCountry]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [providersRes, ordersRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/delivery/admin/providers?country=${selectedCountry}`),
        axios.get(`${API_URL}/api/delivery/admin/orders?limit=20`),
        axios.get(`${API_URL}/api/delivery/admin/stats?country=${selectedCountry}`),
      ]);
      setProviders(providersRes.data.providers || []);
      setOrders(ordersRes.data.orders || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to fetch delivery data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProvider = async () => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        coverage_areas: formData.coverage_areas.split(',').map(a => a.trim()).filter(Boolean),
      };
      await axios.post(`${API_URL}/api/delivery/admin/providers`, payload);
      setShowAddDialog(false);
      resetForm();
      fetchData();
    } catch (err) {
      alert('Failed to add provider. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateProvider = async () => {
    if (!selectedProvider) return;
    setSaving(true);
    try {
      const payload = {
        ...formData,
        coverage_areas: typeof formData.coverage_areas === 'string' 
          ? formData.coverage_areas.split(',').map(a => a.trim()).filter(Boolean)
          : formData.coverage_areas,
      };
      await axios.put(`${API_URL}/api/delivery/admin/providers/${selectedProvider.id}`, payload);
      setShowEditDialog(false);
      setSelectedProvider(null);
      resetForm();
      fetchData();
    } catch (err) {
      alert('Failed to update provider. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleProvider = async (providerId) => {
    try {
      await axios.put(`${API_URL}/api/delivery/admin/providers/${providerId}/toggle`);
      fetchData();
    } catch (err) {
      alert('Failed to toggle provider status.');
    }
  };

  const handleDeleteProvider = async (providerId) => {
    if (!window.confirm('Are you sure you want to delete this delivery provider?')) return;
    try {
      await axios.delete(`${API_URL}/api/delivery/admin/providers/${providerId}`);
      fetchData();
    } catch (err) {
      alert('Failed to delete provider.');
    }
  };

  const openEditDialog = (provider) => {
    setSelectedProvider(provider);
    setFormData({
      business_name: provider.business_name || '',
      contact_name: provider.contact_name || '',
      contact_phone: provider.contact_phone || '',
      contact_email: provider.contact_email || '',
      country: provider.country || 'SL',
      coverage_areas: Array.isArray(provider.coverage_areas) ? provider.coverage_areas.join(', ') : '',
      pricing_per_km: provider.pricing_per_km || 25,
      base_price: provider.base_price || 150,
      is_third_party: provider.is_third_party || false,
      third_party_api: provider.third_party_api || '',
      services: provider.services || ['pickup', 'delivery'],
      vehicle_types: provider.vehicle_types || ['bike', 'car'],
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      business_name: '',
      contact_name: '',
      contact_phone: '',
      contact_email: '',
      country: selectedCountry,
      coverage_areas: '',
      pricing_per_km: 25,
      base_price: 150,
      is_third_party: false,
      third_party_api: '',
      services: ['pickup', 'delivery'],
      vehicle_types: ['bike', 'car'],
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700',
      confirmed: 'bg-blue-100 text-blue-700',
      picked_up: 'bg-purple-100 text-purple-700',
      processing: 'bg-indigo-100 text-indigo-700',
      out_for_delivery: 'bg-orange-100 text-orange-700',
      delivered: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-delivery-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
            <Truck className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Delivery Management</h2>
            <p className="text-sm text-gray-500">Manage delivery providers and orders</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={selectedCountry} onValueChange={setSelectedCountry}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COUNTRIES.map((c) => (
                <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Truck className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Providers</p>
                  <p className="text-2xl font-bold">{stats.providers?.total || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Active Providers</p>
                  <p className="text-2xl font-bold">{stats.providers?.active || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Package className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Orders</p>
                  <p className="text-2xl font-bold">{stats.orders?.total || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Completion Rate</p>
                  <p className="text-2xl font-bold">{stats.orders?.completion_rate || 0}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="providers">
            <Truck className="h-4 w-4 mr-2" />
            Providers
          </TabsTrigger>
          <TabsTrigger value="orders">
            <Package className="h-4 w-4 mr-2" />
            Recent Orders
          </TabsTrigger>
        </TabsList>

        {/* Providers Tab */}
        <TabsContent value="providers" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Delivery Providers - {COUNTRIES.find(c => c.code === selectedCountry)?.name}</CardTitle>
                <CardDescription>Manage pickup and delivery service providers</CardDescription>
              </div>
              <Button onClick={() => { resetForm(); setShowAddDialog(true); }}>
                <Plus className="h-4 w-4 mr-2" />
                Add Provider
              </Button>
            </CardHeader>
            <CardContent>
              {providers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Truck className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No delivery providers found for {COUNTRIES.find(c => c.code === selectedCountry)?.name}</p>
                  <Button variant="outline" className="mt-4" onClick={() => { resetForm(); setShowAddDialog(true); }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Provider
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Provider</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Coverage</TableHead>
                      <TableHead>Pricing</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Rating</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {providers.map((provider) => (
                      <TableRow key={provider.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{provider.business_name}</p>
                            <p className="text-xs text-gray-500">{provider.id}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {provider.contact_phone}
                            </p>
                            {provider.contact_email && (
                              <p className="flex items-center gap-1 text-gray-500">
                                <Mail className="h-3 w-3" />
                                {provider.contact_email}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-[150px]">
                            {(provider.coverage_areas || []).slice(0, 2).map((area, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                <MapPin className="h-2 w-2 mr-1" />
                                {area}
                              </Badge>
                            ))}
                            {(provider.coverage_areas || []).length > 2 && (
                              <Badge variant="outline" className="text-xs">
                                +{provider.coverage_areas.length - 2}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p>Base: {formatCurrency(provider.base_price || 150)}</p>
                            <p className="text-gray-500">{formatCurrency(provider.pricing_per_km || 25)}/km</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {provider.is_third_party ? (
                            <Badge className="bg-purple-100 text-purple-700">
                              <ExternalLink className="h-3 w-3 mr-1" />
                              {provider.third_party_api || '3rd Party'}
                            </Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-700">
                              Own Fleet
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                            <span>{provider.rating?.toFixed(1) || '4.5'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={provider.is_active !== false}
                            onCheckedChange={() => handleToggleProvider(provider.id)}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditDialog(provider)}
                            >
                              <Edit2 className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleDeleteProvider(provider.id)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Delivery Orders</CardTitle>
              <CardDescription>Latest 20 delivery orders across all providers</CardDescription>
            </CardHeader>
            <CardContent>
              {orders.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No delivery orders yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order ID</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Pickup</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell className="font-mono text-sm">{order.id}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {order.delivery_type?.replace(/_/g, ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p>{order.pickup_address?.city}</p>
                            <p className="text-gray-500 text-xs">{order.preferred_pickup_date}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(order.status)}>
                            {order.status?.replace(/_/g, ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {new Date(order.created_at).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Provider Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-blue-500" />
              Add Delivery Provider
            </DialogTitle>
            <DialogDescription>
              Add a new pickup & delivery service provider
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Business Name *</Label>
                <Input
                  placeholder="e.g., SpeedyRide Delivery"
                  value={formData.business_name}
                  onChange={(e) => setFormData({...formData, business_name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Country</Label>
                <Select value={formData.country} onValueChange={(v) => setFormData({...formData, country: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact Name *</Label>
                <Input
                  placeholder="Manager name"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Phone *</Label>
                <Input
                  placeholder="07X XXX XXXX"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email (Optional)</Label>
              <Input
                type="email"
                placeholder="contact@provider.com"
                value={formData.contact_email}
                onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label>Coverage Areas (comma-separated)</Label>
              <Input
                placeholder="Colombo, Kandy, Galle"
                value={formData.coverage_areas}
                onChange={(e) => setFormData({...formData, coverage_areas: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Base Price (Rs)</Label>
                <Input
                  type="number"
                  value={formData.base_price}
                  onChange={(e) => setFormData({...formData, base_price: Number(e.target.value)})}
                />
              </div>
              <div className="space-y-2">
                <Label>Price per KM (Rs)</Label>
                <Input
                  type="number"
                  value={formData.pricing_per_km}
                  onChange={(e) => setFormData({...formData, pricing_per_km: Number(e.target.value)})}
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.is_third_party}
                onCheckedChange={(v) => setFormData({...formData, is_third_party: v})}
              />
              <Label>Third-party Integration (PickMe, Uber, etc.)</Label>
            </div>

            {formData.is_third_party && (
              <div className="space-y-2">
                <Label>API Integration</Label>
                <Select value={formData.third_party_api} onValueChange={(v) => setFormData({...formData, third_party_api: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select integration" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pickme">PickMe</SelectItem>
                    <SelectItem value="uber">Uber Connect</SelectItem>
                    <SelectItem value="grab">Grab</SelectItem>
                    <SelectItem value="lalamove">Lalamove</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddProvider}
              disabled={saving || !formData.business_name || !formData.contact_name || !formData.contact_phone}
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Add Provider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Provider Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-blue-500" />
              Edit Delivery Provider
            </DialogTitle>
            <DialogDescription>
              Update provider details
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Business Name *</Label>
                <Input
                  value={formData.business_name}
                  onChange={(e) => setFormData({...formData, business_name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Country</Label>
                <Select value={formData.country} onValueChange={(v) => setFormData({...formData, country: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact Name *</Label>
                <Input
                  value={formData.contact_name}
                  onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Phone *</Label>
                <Input
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label>Coverage Areas (comma-separated)</Label>
              <Input
                value={formData.coverage_areas}
                onChange={(e) => setFormData({...formData, coverage_areas: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Base Price (Rs)</Label>
                <Input
                  type="number"
                  value={formData.base_price}
                  onChange={(e) => setFormData({...formData, base_price: Number(e.target.value)})}
                />
              </div>
              <div className="space-y-2">
                <Label>Price per KM (Rs)</Label>
                <Input
                  type="number"
                  value={formData.pricing_per_km}
                  onChange={(e) => setFormData({...formData, pricing_per_km: Number(e.target.value)})}
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.is_third_party}
                onCheckedChange={(v) => setFormData({...formData, is_third_party: v})}
              />
              <Label>Third-party Integration</Label>
            </div>

            {formData.is_third_party && (
              <div className="space-y-2">
                <Label>API Integration</Label>
                <Select value={formData.third_party_api} onValueChange={(v) => setFormData({...formData, third_party_api: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select integration" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pickme">PickMe</SelectItem>
                    <SelectItem value="uber">Uber Connect</SelectItem>
                    <SelectItem value="grab">Grab</SelectItem>
                    <SelectItem value="lalamove">Lalamove</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateProvider}
              disabled={saving || !formData.business_name || !formData.contact_name || !formData.contact_phone}
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDeliveryManagement;
